import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import View from 'ol/View';
import dayjs from 'dayjs';
import {
  Box,
  Divider,
  LinearProgress,
  Typography,
  Zoom,
  styled,
} from '@mui/material';
import {DetectInfo} from '../detect-info.jsx';
import {Drawer, RightFrame} from '../../components/modal.js';
import {ExportLink, Toolbar} from '../review-common.jsx';
import {InsetMap} from './InsetMap.jsx';
import {ProgressCircle} from '../../components/ProgressCircle.jsx';
import {YayOrNay} from '../../components/YayOrNay.jsx';
import {fetchSeriesMosaics} from '../../api.js';
import {useQuery} from '@tanstack/react-query';

function getTemporalNeighbors(dateString, basemaps, before = 5, after = 0) {
  // Based on https://hello.planet.com/jira/browse/WEBAPPS-2000 the dateString will
  // will correspond to the time period it was analyzed (i.e. first_acquired of the basemap)
  const idx = basemaps.findIndex(basemap =>
    dayjs(basemap.first_acquired).isSame(dayjs(dateString)),
  );

  const neighbors = [];
  for (let i = before; i > 0; i--) {
    neighbors.push(basemaps[idx - i]);
  }
  neighbors.push(basemaps[idx]);
  for (let i = 0; i < after; i++) {
    neighbors.push(basemaps[idx + i + 1]);
  }

  // out of range basemaps will be undefined in the array,
  //  this lets the down stream code have a unique ID
  //  and test for the "basemap" definition in the object.
  return neighbors.map((basemap, i) => {
    if (basemap) {
      return {
        id: basemap.id,
        basemap,
      };
    } else {
      return {
        id: `basemap-${i}`,
      };
    }
  });
}

const cleanForExport = detects =>
  detects.map(detect => ({
    ...detect,
    properties: {
      ...detect.properties,
      // the export routine expects _include to be true or false,
      //  as to whether to include the feature
      _include: detect.properties._include !== undefined,
      // The selection itself is of the correct source mosaic,
      //  this updates that property to what the user selected.
      source_mosaic_name:
        detect.properties._include !== undefined
          ? detect.properties._include
          : detect.properties.source_mosaic_name,
    },
  }));

const PhoneButton = styled(Box)(({theme}) => ({
  display: 'inline-flex',
  fontSize: 14,
  margin: theme.spacing(0.5),
  borderRadius: theme.spacing(0.5),
  backgroundColor: theme.palette.grey[700],
  width: 80,
  height: 44,
  alignItems: 'center',
  justifyContent: 'center',
}));

PhoneButton.propTypes = {
  value: PropTypes.number,
};

const PhoneDial = () => (
  <Box>
    {[
      [1, 2, 3],
      [4, 5, 6],
    ].map(row => (
      <Box textAlign="center" key={JSON.stringify(row)}>
        {row.map(k => (
          <PhoneButton key={k}>({k})</PhoneButton>
        ))}
      </Box>
    ))}
    <Box textAlign="center">
      <PhoneButton>(0 or R)</PhoneButton>
    </Box>
  </Box>
);

export const ReviewChangeStep = ({
  seriesInfo,
  feed,
  detections,
  onExport,
  onClose,
}) => {
  const [position, setPosition] = useState(1);
  const [detects, setDetects] = useState([...detections]);
  const [doNext, setDoNext] = useState(true);
  const timeoutRef = useRef();
  const sharedMapView = new View();

  const {data: basemaps, isLoading: basemapsLoading} = useQuery({
    queryKey: ['mosaics', seriesInfo.id],
    queryFn: () => fetchSeriesMosaics(seriesInfo.id),
  });

  useEffect(() => {
    setPosition(1);
    setDetects([...detections]);
  }, [detections]);

  const flagDetect = useCallback(
    flag => {
      const nextDetects = [...detects];
      nextDetects[position - 1].properties = {
        ...nextDetects[position - 1].properties,
        _include: flag,
      };
      setDetects(nextDetects);

      // the time out is used to give the selection
      //  time to "transition" and give positive feedback to the user.
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        if (position >= detects.length) {
          onExport(cleanForExport(nextDetects));
        } else {
          setDoNext(false);
          setPosition(position + 1);
        }
      }, 500);
    },
    [detects, position, onExport],
  );

  const exportSelected = useCallback(() => {
    onExport(cleanForExport(detects.slice(0, position)));
  }, [detects, position, onExport]);

  const exportAll = useCallback(() => {
    const all = detections.map(feature => ({
      ...feature,
      properties: {
        ...feature.properties,
        _include: feature.properties.source_mosaic_name,
      },
    }));
    onExport(cleanForExport(all));
  }, [detections, onExport]);

  const neighbors = useMemo(() => {
    if (!basemapsLoading && basemaps) {
      return getTemporalNeighbors(
        detects[position - 1].properties.observed,
        basemaps,
      );
    }
    return [];
  }, [detects, basemapsLoading, basemaps, position]);

  useEffect(() => {
    const keyFn = evt => {
      const k = evt.key;

      // non integers will return as NaN
      //  which is false for integer comparison
      const keyValue = parseInt(k);
      if (k === 'r' || keyValue === 0) {
        flagDetect(undefined);
      } else if (1 <= keyValue && keyValue <= 6) {
        flagDetect(neighbors[keyValue - 1].basemap.name);
      }
    };

    window.addEventListener('keypress', keyFn);
    return () => {
      window.removeEventListener('keypress', keyFn);
    };
  }, [flagDetect, neighbors]);

  return (
    <React.Fragment>
      <Drawer>
        <Box display="flex" flexDirection="column" flex={1} padding={4}>
          <Box flex={1}>
            <Typography variant="h5">Review detections for download</Typography>
            <Box py={3}>
              <Typography variant="body1" gutterBottom>
                Select the detection to the right where the change occured.
              </Typography>
            </Box>
            <PhoneDial />
          </Box>
        </Box>

        <Box paddingBottom={2} marginTop="auto">
          <Box paddingX={4} paddingBottom={4}>
            <DetectInfo detect={detects[position - 1]} />
          </Box>

          <Divider />

          <Box paddingX={4} paddingTop={2}>
            <ProgressCircle
              value={position}
              maxValue={detections ? detections.length : 0}
              size={22 * 8}
            />
            <Typography
              variant="overline"
              component="div"
              sx={{marginTop: '8px', textAlign: 'center'}}
            >
              Detects reviewed
            </Typography>
          </Box>
          <Box marginTop={2} marginBottom={1} textAlign="center">
            <Typography variant="body1" gutterBottom>
              Want to skip this step?
            </Typography>
            <Typography variant="body2">
              <ExportLink onClick={exportAll}>Export all</ExportLink>
              {' or '}
              <ExportLink onClick={exportSelected}>
                Export reviewed detections
              </ExportLink>
            </Typography>
          </Box>
        </Box>
      </Drawer>
      <RightFrame sx={{overflow: 'hidden'}}>
        <Toolbar
          onBack={() => setPosition(Math.max(1, position - 1))}
          onClose={() => onClose()}
        />

        <Box
          position="relative"
          flexDirection="column"
          flex={1}
          px={3}
          overflow="hidden"
        >
          {basemapsLoading && <LinearProgress />}
          {neighbors.map(basemap => (
            <Zoom
              in={doNext}
              onExited={() => setDoNext(true)}
              key={basemap.id}
              timeout={200}
            >
              <Box
                display="inline-block"
                position="relative"
                width="33%"
                height="50%"
              >
                {basemap.basemap && (
                  <Box position="absolute" top={20} right={20} zIndex={20}>
                    <YayOrNay
                      include={
                        detects[position - 1].properties._include !== undefined
                          ? detects[position - 1].properties._include ===
                            basemap.basemap.name
                            ? true
                            : undefined
                          : undefined
                      }
                      onClick={() => {
                        flagDetect(basemap.basemap.name);
                      }}
                    />
                  </Box>
                )}
                <InsetMap
                  basemap={basemap.basemap}
                  detect={detects[position - 1]}
                  feed={feed}
                  view={sharedMapView}
                />
                <Box
                  position="absolute"
                  bottom={6}
                  right={0}
                  zIndex={20}
                  width="calc(100% - 16px)"
                  display="flex"
                >
                  <Typography
                    variant="caption"
                    noWrap
                    title={basemap.basemap.name}
                  >
                    {basemap.basemap.name}
                  </Typography>
                </Box>
              </Box>
            </Zoom>
          ))}
        </Box>
        <Box py={3} display="flex" alignItems="center" justifyContent="center">
          {/* The absolute position below was used to prevent some
           * reposition quirks with the flex layout when the checkmark
           * would disappear.
           */}
          <Box
            display="inline-flex"
            padding={2}
            borderRadius="20px"
            bgcolor="rgba(155, 50, 50, 0.5)"
            alignItems="baseline"
            position="relative"
            width={210}
            height={80}
          >
            <Box
              position="absolute"
              bottom={29}
              left={20}
              fontSize={18}
              fontWeight="bold"
              height="24px"
              marginRight={2.5}
            >
              No change
            </Box>
            <Box position="absolute" bottom={20} right={20}>
              <YayOrNay
                include={
                  detects[position - 1].properties._include === undefined
                    ? true
                    : undefined
                }
                onClick={() => {
                  // undefined is used to capture the default change state.
                  flagDetect(undefined);
                }}
              />
            </Box>
          </Box>
        </Box>
      </RightFrame>
    </React.Fragment>
  );
};

ReviewChangeStep.propTypes = {
  detections: PropTypes.array,
  feed: PropTypes.object,
  onExport: PropTypes.func,
  onClose: PropTypes.func,
  seriesInfo: PropTypes.object,
};
